import { faBookOpen, faFileContract } from "@fortawesome/pro-duotone-svg-icons"
import useServiceTicketsEnabled from '../../hooks/useServiceTicketsEnabled'
import { faChevronLeft, faChevronRight, faTimes } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button, Typography } from "antd"
import { useAccountSetup } from "./AccountSetupContext"
import RichTextEditor from "../RichTextEditor/RichTextEditor"
import styled from "styled-components"
import './AccountSetupModal.css'
import { useUpdateTermsAndConditionsMutation } from "../../domain/proposals/settings/proposalSettingsQueries"

const { Title, Text } = Typography

export type Props = {
  onBack: () => void
  onExit: () => void
  onComplete: () => void
}

const LegalDisclaimer = styled.div`
  background: #fff7e6;
  border: 1px solid #ffd591;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 16px;
`

const InfoBox = styled.div`
  position: absolute;
  top: 50%;
  left: 48px;
  right: 48px;
  transform: translateY(-50%);
  z-index: 2;
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1);
`

const InfoBoxHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
`

const InfoBoxTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
`

const InfoBoxSubtitle = styled.p`
  font-size: 13px;
  color: #595959;
  margin: 0 0 16px 0;
  line-height: 1.4;
`

const InfoBoxSection = styled.div`
  margin-bottom: 16px;
`

const InfoBoxSectionTitle = styled.h4`
  font-size: 14px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 8px 0;
`

const BulletList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`

const BulletItem = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: #1a1a1a;
  margin-bottom: 6px;

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    background: #FF5C35;
    border-radius: 50%;
    flex-shrink: 0;
  }
`

const RememberBox = styled.div`
  background: #fffbe6;
  border: 1px solid #ffe58f;
  border-radius: 8px;
  padding: 12px 16px;
  margin-top: 4px;
`

const RememberTitle = styled.span`
  font-weight: 700;
  font-size: 13px;
  color: #1a1a1a;
  display: block;
  margin-bottom: 2px;
`

const RememberText = styled.span`
  font-size: 12px;
  color: #595959;
  line-height: 1.4;
`

const AccountSetupTermsStep = ({ onBack, onExit, onComplete }: Props) => {
  const showServiceTickets = useServiceTicketsEnabled()
  const { state, dispatch } = useAccountSetup()
  const { termsAndConditions } = state
  const updateTermsMutation = useUpdateTermsAndConditionsMutation()

  const handleFinish = () => {
    console.log('Saving terms and conditions:', termsAndConditions.content.substring(0, 100) + '...')
    updateTermsMutation.mutate({
      projectsTerms: termsAndConditions.content,
      maintenanceTerms: termsAndConditions.content,
      serviceTicketsTerms: termsAndConditions.content
    }, {
      onSuccess: () => {
        console.log('Terms and conditions saved successfully')
        onComplete()
      },
      onError: (error) => {
        console.error('Failed to save terms and conditions:', error)
      }
    })
  }

  return (
    <div className="account-setup-modal-content">
      <div className="account-setup-progress-bar">
        <div className="account-setup-progress-fill" style={{ width: '75%' }} />
      </div>
      <button
        className="account-setup-exit-button"
        onClick={onExit}
        aria-label="Exit setup"
      >
        <FontAwesomeIcon icon={faTimes} />
      </button>

      <div className="account-setup-modal-left account-setup-form-container">
        <div className="account-setup-form-header">
          <div className="account-setup-modal-icon">
            <FontAwesomeIcon icon={faFileContract} size="2x" />
          </div>
          <Title level={3} style={{ marginTop: 16, marginBottom: 8 }}>
            Proposal Terms and Conditions
          </Title>
          <Text type="secondary" style={{ fontSize: 14 }}>
            Set your standard terms and conditions that will appear on all proposals. This helps protect your business and set clear expectations with customers.
          </Text>
        </div>

        <div className="account-setup-form" style={{ marginTop: 24 }}>
          <LegalDisclaimer>
            <Text strong style={{ color: '#d46b08' }}>Legal Disclaimer</Text>
            <br />
            <Text style={{ fontSize: 13, color: '#8c5a00' }}>
              We recommend consulting with legal counsel to ensure your terms and conditions are appropriate for your business. These templates are not legal advice.
            </Text>
          </LegalDisclaimer>

          <Text strong style={{ display: 'block', marginBottom: 8 }}>Terms and Conditions</Text>
          <div style={{ border: '1px solid #d9d9d9', borderRadius: 8 }}>
            <RichTextEditor
              value={termsAndConditions.content}
              onChange={(value) => dispatch({ type: 'UPDATE_TERMS', payload: value })}
              placeholder="Enter your terms and conditions here..."
              style={{ minHeight: 200 }}
            />
          </div>

          <div className="account-setup-form-buttons" style={{ marginTop: 32 }}>
            <Button size="large" onClick={onBack}>
              <FontAwesomeIcon icon={faChevronLeft} /> Back
            </Button>
            <Button
              type="primary"
              size="large"
              onClick={handleFinish}
              loading={updateTermsMutation.isLoading}
            >
              Next <FontAwesomeIcon icon={faChevronRight} />
            </Button>
          </div>
        </div>
      </div>

      <div className="account-setup-modal-right">
        <img
          src="/images/account-setup/step-6.png"
          alt="Terms and conditions"
          className="account-setup-modal-image"
        />
        <div className="account-setup-modal-logo">
          <img src="/images/synkedup-dark.png" alt="SynkedUP" />
        </div>
        <InfoBox>
          <InfoBoxHeader>
            <FontAwesomeIcon icon={faBookOpen} style={{ color: '#FF5C35', fontSize: 22 }} />
            <InfoBoxTitle>Terms and Conditions Best Practices</InfoBoxTitle>
          </InfoBoxHeader>
          <InfoBoxSubtitle>
            Well-written terms and conditions protect your business and set clear expectations with customers.
          </InfoBoxSubtitle>

          <InfoBoxSection>
            <InfoBoxSectionTitle>Essential Elements to Include:</InfoBoxSectionTitle>
            <BulletList>
              <BulletItem>Payment terms and late fees</BulletItem>
              <BulletItem>Proposal validity period</BulletItem>
              <BulletItem>Change order procedures</BulletItem>
              <BulletItem>Weather delay policies</BulletItem>
              <BulletItem>Warranty information</BulletItem>
              <BulletItem>Liability limitations</BulletItem>
              <BulletItem>Customer responsibilities</BulletItem>
            </BulletList>
          </InfoBoxSection>

          <InfoBoxSection style={{ marginBottom: 12 }}>
            <InfoBoxSectionTitle>Writing Tips</InfoBoxSectionTitle>
            <BulletList>
              <BulletItem>Use clear, simple language</BulletItem>
              <BulletItem>Be specific about timelines</BulletItem>
              <BulletItem>Address common scenarios</BulletItem>
              <BulletItem>Include dispute resolution</BulletItem>
              <BulletItem>Make terms easily readable</BulletItem>
            </BulletList>
          </InfoBoxSection>

          <RememberBox>
            <RememberTitle>Remember:</RememberTitle>
            <RememberText>
              These terms apply to all job types ({showServiceTickets ? 'projects, service tickets, and maintenance' : 'projects and maintenance'}). Consider having your attorney review them before using.
            </RememberText>
          </RememberBox>
        </InfoBox>
      </div>
    </div>
  )
}

export default AccountSetupTermsStep
